import { clsx } from "clsx";
import {
	Boxes,
	Building2,
	Check,
	ChevronDown,
	ChevronRight,
	Plus,
	RotateCw,
	TriangleAlert,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveCanteiro } from "@/features/canteiros/hooks/use-active-canteiro.ts";
import { ObraFormDialog } from "@/features/stages/components/obra-form-dialog";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { type Obra, obraSwitchPath } from "@/features/stages/obra.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

export function ContextBreadcrumb() {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		canteiro: activeCanteiro,
		canteiros,
		isLoading: canteirosLoading,
		isError: canteirosError,
		refetch: refetchCanteiros,
	} = useActiveCanteiro();
	const {
		obra: activeObra,
		obras,
		isLoading: obrasLoading,
		isError: obrasError,
		refetch: refetchObras,
	} = useActiveObra();
	const { has } = usePermissions();
	const canCreateObra = has("constructions:create");
	const [newObraOpen, setNewObraOpen] = useState(false);

	const canteiroObras = obras.filter(
		(o) => o.canteiroId === activeCanteiro?.id,
	);

	const anyState =
		!!activeObra ||
		obrasLoading ||
		obrasError ||
		canteirosLoading ||
		canteirosError;
	if (!anyState) return null;

	function selectCanteiro(id: string) {
		if (id === activeCanteiro?.id) return;
		const target = obras.find((o) => o.canteiroId === id);
		if (target) navigate(obraSwitchPath(target, location));
	}

	function selectObra(obra: Obra) {
		navigate(obraSwitchPath(obra, location));
	}

	function handleObraCreated(id: string) {
		navigate(`/${id}/etapas`);
	}

	return (
		<div className="context-breadcrumb">
			<ContextSlot
				collapse
				loading={canteirosLoading}
				error={canteirosError}
				retryLabel="Recarregar canteiros"
				onRetry={refetchCanteiros}
				icon={<Boxes className="context-breadcrumb__icon" />}
				label={activeCanteiro?.name}
				empty="Sem canteiro"
				triggerLabel="Trocar de canteiro"
			>
				{canteiros.map((c) => (
					<DropdownMenuItem
						key={c.id}
						onClick={() => selectCanteiro(c.id)}
						className={clsx(
							"context-breadcrumb__item",
							c.id === activeCanteiro?.id && "is-active",
						)}
					>
						<span className="context-breadcrumb__item-name">{c.name}</span>
						{c.id === activeCanteiro?.id && <Check />}
					</DropdownMenuItem>
				))}
			</ContextSlot>

			<ChevronRight className="context-breadcrumb__sep" />

			<ContextSlot
				loading={obrasLoading}
				error={obrasError}
				retryLabel="Recarregar obras"
				onRetry={refetchObras}
				icon={<Building2 className="context-breadcrumb__icon" />}
				label={activeObra?.name}
				empty="Sem obra"
				triggerLabel="Trocar de obra"
			>
				{canteiroObras.map((o) => (
					<DropdownMenuItem
						key={o.id}
						onClick={() => selectObra(o)}
						className={clsx(
							"context-breadcrumb__item",
							o.id === activeObra?.id && "is-active",
						)}
					>
						<span className="context-breadcrumb__item-name">{o.name}</span>
						{o.id === activeObra?.id && <Check />}
					</DropdownMenuItem>
				))}
				{canCreateObra && (
					<>
						{canteiroObras.length > 0 && <DropdownMenuSeparator />}
						<DropdownMenuItem onClick={() => setNewObraOpen(true)}>
							<Plus />
							Nova obra
						</DropdownMenuItem>
					</>
				)}
			</ContextSlot>

			{canCreateObra && (
				<ObraFormDialog
					open={newObraOpen}
					onOpenChange={setNewObraOpen}
					onCreated={handleObraCreated}
				/>
			)}
		</div>
	);
}

function ContextSlot({
	collapse = false,
	loading,
	error,
	retryLabel,
	onRetry,
	icon,
	label,
	empty,
	triggerLabel,
	children,
}: {
	collapse?: boolean;
	loading: boolean;
	error: boolean;
	retryLabel: string;
	onRetry: () => void;
	icon: ReactNode;
	label?: string;
	empty: string;
	triggerLabel: string;
	children: ReactNode;
}) {
	const collapseClass = collapse ? "context-breadcrumb__collapse" : undefined;

	if (loading) {
		return (
			<Skeleton
				className={clsx("context-breadcrumb__skeleton", collapseClass)}
			/>
		);
	}

	if (error) {
		return (
			<button
				type="button"
				aria-label={retryLabel}
				className={clsx("context-breadcrumb__chip", "is-error", collapseClass)}
				onClick={onRetry}
			>
				<TriangleAlert className="context-breadcrumb__icon" />
				<span className="context-breadcrumb__name">Indisponível</span>
				<RotateCw className="context-breadcrumb__chevron" />
			</button>
		);
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					aria-label={triggerLabel}
					className={clsx(
						"context-breadcrumb__chip",
						!label && "is-empty",
						collapseClass,
					)}
				>
					{icon}
					<span className="context-breadcrumb__name">{label ?? empty}</span>
					<ChevronDown className="context-breadcrumb__chevron" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="context-breadcrumb__menu">
				{children}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
